import React, { useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import ScreenBurner from "./ScreenBurner";
import { useAuth } from "../../lib/auth";
import { getBrand } from "../../config/brandIdentity";
import "../../styles/robotics-tokens.css";

/**
 * Swap the browser tab favicon + web-app manifest to the Robotics mark
 * on mount, and restore whatever was there before on unmount. This keeps
 * Level 2.5 isolation: STEAM routes never see the Robotics mark, and the
 * global /public/index.html is not modified.
 */
function useRoboticsFavicon() {
  useEffect(() => {
    const head = document.head;
    const specs = [
      { rel: "icon",             type: "image/svg+xml", sizes: "any",       href: "/robotics/brand/favicon.svg" },
      { rel: "icon",             type: "image/png",     sizes: "32x32",     href: "/robotics/brand/favicon-32.png" },
      { rel: "icon",             type: "image/png",     sizes: "16x16",     href: "/robotics/brand/favicon-16.png" },
      { rel: "apple-touch-icon", type: "image/png",     sizes: "180x180",   href: "/robotics/brand/apple-touch-icon.png" },
      { rel: "manifest",         type: null,            sizes: null,        href: "/robotics/brand/site.webmanifest" },
    ];
    const MARK = "data-rbx-icon";
    // Snapshot pre-existing icon/manifest links so we can restore later.
    const preexisting = Array.from(
      head.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"], link[rel="manifest"]')
    ).filter((el) => !el.hasAttribute(MARK));
    preexisting.forEach((el) => (el.dataset.rbxSuspended = "1"));

    const added = specs.map(({ rel, type, sizes, href }) => {
      const link = document.createElement("link");
      link.rel = rel;
      if (type) link.type = type;
      if (sizes) link.setAttribute("sizes", sizes);
      link.href = href;
      link.setAttribute(MARK, "1");
      head.appendChild(link);
      return link;
    });

    // Optional: theme-color for the browser chrome (mobile)
    const themeMeta = document.createElement("meta");
    themeMeta.name = "theme-color";
    themeMeta.content = "#0d2340";
    themeMeta.setAttribute(MARK, "1");
    head.appendChild(themeMeta);

    return () => {
      added.forEach((el) => el.remove());
      themeMeta.remove();
      preexisting.forEach((el) => delete el.dataset.rbxSuspended);
    };
  }, []);
}

/**
 * RoboticsShell — shared page frame for every /robotics/* route.
 *
 *  - Adds the persistent Screen Burner ("channel bug") watermark.
 *  - Applies the `.rbx--soft` modifier for warm-palette pages
 *    (Girls@TEC, For-Parents) when `soft` prop is true.
 *  - Footer swaps Sign in ↔ Sign out based on shared auth context.
 */
export default function RoboticsShell({ children, gridBackdrop = false, soft = false }) {
  useRoboticsFavicon();
  const { user, logout } = useAuth();
  const signedIn = user && user !== false;
  const brand = getBrand();
  const tagline = brand.formal.replace(/^The /, "").toUpperCase();
  const cls = ["rbx"];
  if (gridBackdrop) cls.push("rbx--grid");
  if (soft) cls.push("rbx--soft");
  return (
    <div className={cls.join(" ")} data-testid="rbx-shell" data-soft={soft ? "1" : "0"}>
      <ScreenBurner variant={soft ? "soft" : "bold"} />
      <nav className="rbx-nav" aria-label="Robotics primary">
        <Link to="/robotics" className="rbx-nav__brand" data-testid="rbx-brand">
          <span className="rbx-nav__brand-mark" aria-hidden="true" />
          <span className="rbx-nav__brand-stack">
            <span className="rbx-nav__brand-name">FutureTec Robotics</span>
            <span className="rbx-nav__brand-tagline" data-testid="rbx-brand-tagline">{tagline}</span>
          </span>
        </Link>
        <div className="rbx-nav__links">
          <NavLink to="/robotics" end className={({ isActive }) => isActive ? "is-active" : ""} data-testid="rbx-nav-home">Home</NavLink>
          <NavLink to="/robotics/meet" className={({ isActive }) => isActive ? "is-active" : ""} data-testid="rbx-nav-meet">Meet</NavLink>
          <NavLink to="/robotics/showcase" className={({ isActive }) => isActive ? "is-active" : ""} data-testid="rbx-nav-showcase">Showcase</NavLink>
          <NavLink to="/robotics/girls-at-tec" className={({ isActive }) => isActive ? "is-active" : ""} data-testid="rbx-nav-girls">Girls@TEC</NavLink>
          <NavLink to="/robotics/for-parents" className={({ isActive }) => isActive ? "is-active" : ""} data-testid="rbx-nav-parents">Parents</NavLink>
          <NavLink to="/robotics/schools" className={({ isActive }) => isActive ? "is-active" : ""} data-testid="rbx-nav-schools">Schools</NavLink>
          <NavLink to="/robotics/qualifications" className={({ isActive }) => isActive ? "is-active" : ""} data-testid="rbx-nav-qualifications">Qualifications</NavLink>
          <NavLink to="/robotics/dashboard" className={({ isActive }) => isActive ? "is-active" : ""} data-testid="rbx-nav-dashboard">Dashboard</NavLink>
        </div>
      </nav>

      {children}

      <footer className="rbx-footer" data-testid="rbx-footer">
        FTG · ROBOTICS ACADEMY &nbsp;·&nbsp; By the team that organised the first LEGO Robotics Olympiad in Sri Lanka
        &nbsp;·&nbsp; <Link to="/robotics/heritage" data-testid="rbx-footer-heritage">The Clarke Legacy</Link>
        &nbsp;·&nbsp; <Link to="/robotics/about" data-testid="rbx-footer-about">About</Link>
        &nbsp;·&nbsp; <Link to="/robotics/qualifications" data-testid="rbx-footer-qualifications">Qualifications</Link>
        &nbsp;·&nbsp; <a href="/robotics-plan/" target="_blank" rel="noopener noreferrer">Curriculum blueprint</a>
        &nbsp;·&nbsp;{" "}
        {signedIn ? (
          <button
            type="button"
            onClick={logout}
            data-testid="rbx-footer-signout"
            style={{
              background: "none", border: "none", padding: 0,
              color: "var(--rbx-copper)", cursor: "pointer",
              font: "inherit", letterSpacing: "inherit",
            }}
          >
            Sign out
          </button>
        ) : (
          <Link to="/robotics/signin" data-testid="rbx-footer-signin">Sign in</Link>
        )}
      </footer>
    </div>
  );
}
